/**
 * Standard Actions configuration for Dawn.
 *
 * Storefront Renderer injects the Shopify Standard Actions bundle
 * (`window.Shopify.actions.{updateCart,openCart,getCart,…}`). This file
 * overrides the bundle's built-in Dawn refresh path with an explicit,
 * in-theme version so forks that change Dawn's cart contract keep
 * working. Remove this file and the built-in defaults take over.
 *
 *   - openCart   — opens <cart-drawer>; falls back to /cart.
 *   - updateCart — after the Storefront API mutation, refreshes the
 *     affected cart sections and publishes `cart-update` so Dawn's
 *     pubsub subscribers react.
 *   - other actions (getCart, etc.) keep the default implementation.
 */

// Cart custom elements that advertise sections via getSectionsToRender().
// If Dawn adds a new cart custom element, add its tag here.
const DAWN_CART_TAGS = ['cart-drawer', 'cart-items', 'cart-drawer-items', 'cart-notification'];

// Sections that Dawn's own pubsub subscribers refresh (cart.js's
// CartItems#onCartUpdate fetches and replaces these directly when
// cart-update fires; cart-drawer.js's renderContents handles the
// drawer body). We skip them here to avoid double-rendering.
// Format is '<element-tag>:<getSectionsToRender entry id>'.
// If you change which sections those subscribers refresh, update this set.
const DAWN_PUBSUB_REFRESHED_SECTIONS = new Set([
  'cart-drawer:cart-drawer',
  'cart-drawer-items:CartDrawer',
  'cart-items:main-cart-items',
]);

// Walk every mounted Dawn cart custom element, collect the sections it
// wants rendered, and dedupe. Returns a Map keyed by section id, each
// entry pointing at the DOM mount and the selector used to extract the
// fresh fragment.
function collectCartSections() {
  const sections = new Map();

  for (const el of document.querySelectorAll(DAWN_CART_TAGS.join(','))) {
    let entries;
    try {
      entries = el.getSectionsToRender?.();
    } catch {
      continue;
    }

    const tag = el.tagName.toLowerCase();
    for (const entry of entries ?? []) {
      if (DAWN_PUBSUB_REFRESHED_SECTIONS.has(`${tag}:${entry.id}`)) continue;

      const sectionId = entry.section ?? entry.id;
      if (!sectionId || sections.has(sectionId)) continue;

      // Two patterns coexist in getSectionsToRender():
      //   - cart-items style: entry.section is the parent Liquid section
      //     id, entry.selector is a child node inside it.
      //   - cart-drawer / cart-notification style: entry.id IS the mount;
      //     there is no parent wrapper.
      const root = entry.section ? document.getElementById(entry.id) : document;
      if (!root) continue;

      const mount = entry.selector
        ? (root.querySelector(entry.selector) ?? (entry.section ? root : null))
        : document.getElementById(entry.id);
      if (!mount) continue;

      sections.set(sectionId, {
        mount,
        extractSelector: entry.selector || '.shopify-section',
      });
    }
  }

  return sections;
}

// After a Storefront API mutation, refresh every Dawn cart section
// that isn't already refreshed by Dawn's own pubsub subscribers, then
// publish 'cart-update' so the subscribers run.
//
// We always fetch /cart.js (with sections= when we have any) so that
// `cartData` is defined for subscribers. quick-add-bulk.js reads
// `event.cartData.items` unconditionally — publishing without cartData
// makes it throw.
async function refreshDawnCartUI() {
  const sections = collectCartSections();
  const sectionsQuery = sections.size
    ? `?sections=${[...sections.keys()].join(',')}`
    : '';
  // `routes` is a Dawn global, but don't assume it's defined.
  const cartUrl = (typeof routes !== 'undefined' && routes?.cart_url) || '/cart';
  const url = `${cartUrl}.js${sectionsQuery}`;
  const cartData = await fetch(url, { headers: { Accept: 'application/json' } })
    .then((r) => (r.ok ? r.json() : null))
    .catch(() => null);

  if (cartData?.sections) {
    for (const [id, { mount, extractSelector }] of sections) {
      const html = cartData.sections[id];
      if (!html) continue;
      const source = new DOMParser()
        .parseFromString(html, 'text/html')
        .querySelector(extractSelector);
      if (source) mount.replaceChildren(...source.childNodes);
    }
  }

  // Hand off to Dawn's existing subscribers. cartData is the full
  // Cart Ajax payload (items, item_count, token, …) plus sections;
  // quick-add-bulk.js and price-per-item.js read it directly.
  publish(PUB_SUB_EVENTS.cartUpdate, {
    source: 'external-refresh',
    cartData: cartData ?? undefined,
  });
}

function initStandardActions() {
  const actions = window.Shopify?.actions;
  if (!actions) return;

  actions.openCart.configure({
    async handler(defaultHandler) {
      const drawer = document.querySelector('cart-drawer');
      if (drawer && typeof drawer.open === 'function') {
        drawer.open();
        return;
      }
      return defaultHandler();
    },
  });

  actions.updateCart.configure({
    // Dawn doesn't currently listen for shopify:cart:* events, but the
    // bundle requires an eventTarget. document is the conventional root.
    eventTarget: () => document,
    async handler(defaultHandler) {
      const result = await defaultHandler();
      try {
        await refreshDawnCartUI();
      } catch (error) {
        console.error('[Dawn] Standard Actions cart refresh failed; reloading.', error);
        window.location.reload();
      }
      return result;
    },
  });
}

// Run immediately if the standard-actions bundle has already attached
// `Shopify.actions`; otherwise wait for DOMContentLoaded, which fires after
// all module scripts have executed regardless of document order.
if (window.Shopify?.actions) {
  initStandardActions();
} else {
  document.addEventListener('DOMContentLoaded', initStandardActions, { once: true });
}

// Cristal Kitchen — galería de imágenes de producto al pasar el mouse.
// En escritorio, cada tarjeta cambia entre todas sus fotos cada 2 segundos
// con una transición suave tipo fundido. Al retirar el mouse vuelve a la portada.
(() => {
  const CARD_SELECTOR = '.product-card-wrapper';
  const INTERVAL_MS = 2000;
  const hoverMedia = window.matchMedia('(hover: hover) and (pointer: fine)');
  const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

  function ensureGalleryStyles() {
    if (document.getElementById('ck-product-card-gallery-style')) return;

    const style = document.createElement('style');
    style.id = 'ck-product-card-gallery-style';
    style.textContent = `
      @media (hover: hover) and (pointer: fine) {
        .product-card-wrapper.ck-gallery-ready .media.media--hover-effect > img.ck-card-gallery__image {
          opacity: 0 !important;
          transform: scale(1) !important;
          transition: opacity 650ms cubic-bezier(.4, 0, .2, 1), transform 2s ease !important;
          z-index: 1;
        }

        .product-card-wrapper.ck-gallery-ready .media.media--hover-effect > img.ck-card-gallery__image.is-active {
          opacity: 1 !important;
          transform: scale(1.012) !important;
          z-index: 2;
        }
      }

      @media (prefers-reduced-motion: reduce) {
        .product-card-wrapper.ck-gallery-ready .media.media--hover-effect > img.ck-card-gallery__image {
          transition: none !important;
          transform: none !important;
        }
      }
    `;
    document.head.appendChild(style);
  }

  function sizedShopifyImage(src) {
    try {
      const url = new URL(src, window.location.origin);
      url.searchParams.set('width', '900');
      return url.toString();
    } catch {
      return src;
    }
  }

  function productJsonUrl(card) {
    const link = card.querySelector(
      '.card__heading a[href*="/products/"], a.full-unstyled-link[href*="/products/"], a[href*="/products/"]'
    );
    if (!link) return null;

    const url = new URL(link.href, window.location.origin);
    const path = url.pathname.replace(/\.js$/, '').replace(/\/$/, '');
    return `${path}.js`;
  }

  async function prepareGallery(card) {
    if (card._ckGalleryPromise) return card._ckGalleryPromise;

    card._ckGalleryPromise = (async () => {
      const media = card.querySelector('.card__media .media');
      const firstImage = media?.querySelector('img');
      const jsonUrl = productJsonUrl(card);

      if (!media || !firstImage || !jsonUrl) return [];

      const response = await fetch(jsonUrl, {
        credentials: 'same-origin',
        headers: { Accept: 'application/json' },
      });

      if (!response.ok) return [];

      const product = await response.json();
      const images = Array.isArray(product.images)
        ? product.images
            .map((image) => (typeof image === 'string' ? image : image?.src))
            .filter(Boolean)
        : [];

      if (images.length < 2) return [];

      // Quita la imagen secundaria estándar de Dawn para que no interfiera
      // con el fundido de la galería completa.
      [...media.querySelectorAll('img')].slice(1).forEach((img) => img.remove());

      firstImage.classList.add('ck-card-gallery__image', 'is-active');
      firstImage.setAttribute('data-ck-card-image', '0');

      images.slice(1).forEach((src, index) => {
        const img = document.createElement('img');
        img.src = sizedShopifyImage(src);
        img.alt = product.title || firstImage.alt || '';
        img.loading = 'eager';
        img.decoding = 'async';
        img.className = 'motion-reduce ck-card-gallery__image';
        img.setAttribute('data-ck-card-image', String(index + 1));
        media.appendChild(img);
      });

      card.classList.add('ck-gallery-ready');
      return [...media.querySelectorAll('.ck-card-gallery__image')];
    })().catch(() => []);

    return card._ckGalleryPromise;
  }

  function bindCard(card) {
    if (card.dataset.ckGalleryBound === 'true') return;
    card.dataset.ckGalleryBound = 'true';

    let timer = null;
    let startTimer = null;
    let current = 0;
    let hovering = false;

    const show = (images, nextIndex) => {
      if (!images.length) return;
      images[current]?.classList.remove('is-active');
      current = nextIndex;
      images[current]?.classList.add('is-active');
    };

    const stop = async () => {
      hovering = false;
      clearTimeout(startTimer);
      clearInterval(timer);
      startTimer = null;
      timer = null;

      const images = await card._ckGalleryPromise?.catch(() => []);
      if (images?.length && current !== 0) show(images, 0);
    };

    card.addEventListener('mouseenter', async () => {
      if (!hoverMedia.matches || reduceMotion.matches) return;

      hovering = true;
      const images = await prepareGallery(card);
      if (!hovering || images.length < 2) return;

      clearTimeout(startTimer);
      clearInterval(timer);

      // Mantiene la portada 2 segundos antes del primer cambio.
      startTimer = window.setTimeout(() => {
        if (!hovering) return;
        show(images, (current + 1) % images.length);

        timer = window.setInterval(() => {
          if (!hovering) return;
          show(images, (current + 1) % images.length);
        }, INTERVAL_MS);
      }, INTERVAL_MS);
    });

    card.addEventListener('mouseleave', stop);
  }

  function initProductCardGalleries(root = document) {
    ensureGalleryStyles();

    if (root.matches?.(CARD_SELECTOR)) bindCard(root);
    root.querySelectorAll?.(CARD_SELECTOR).forEach(bindCard);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initProductCardGalleries(), { once: true });
  } else {
    initProductCardGalleries();
  }

  document.addEventListener('shopify:section:load', (event) => {
    initProductCardGalleries(event.target);
  });

  const observer = new MutationObserver((mutations) => {
    for (const mutation of mutations) {
      mutation.addedNodes.forEach((node) => {
        if (node.nodeType === 1) initProductCardGalleries(node);
      });
    }
  });

  observer.observe(document.documentElement, { childList: true, subtree: true });
})();
